import React, {useEffect, useMemo, useReducer} from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Label} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {Condition} from '../contracts/report-definition.types';
import {
	ReportTaxonomy,
	fieldByFilterField,
	isValueEditable,
	resolverKindFor,
	ReportTaxonomyField,
} from '../contracts/report-taxonomy.types';
import {formatConditionSummary, SummaryLabels} from './formatConditionSummary';
import {fieldForStoredCondition} from './retiredFilterFields';
import {operatorForValues, operatorsForField} from './conditionOperators';
import {ConditionOperatorPicker} from './ConditionOperatorPicker';
import {ConditionValuePicker} from './ConditionValuePicker';
import {RELAY_BACKED_KINDS, useValueSource} from './valueSources/useValueSource';
import {ResolverKind, ValueSource} from './valueSources/valueSource.types';
import {remainingWorkSelection, remainingWorkWrite} from './remainingWorkCondition';
import {useReportBuilder} from '../ReportBuilderContext';
import {grainOf} from '../reportGrain';

// Only the Relay-backed kinds need the on-mount label warm-up below; the static sources (boolean,
// workflowStatus) already resolve synchronously from their fixed candidate list. The list lives
// next to the registry so the two cannot drift.

/** Mounts `source.useCandidates` purely to trigger and observe its fetch, without rendering
 * anything itself. This lets a tile warm its own value source's label cache for its pre-set
 * values as soon as it mounts, instead of only warming when the user opens the value picker
 * dropdown (which mounts `ConditionValuePicker`, and hence `useCandidates`, separately). Calls
 * `onSettled` whenever the fetch is not in flight, so the parent can recompute its summary once
 * the cache has fresh data. */
/** The field is a fixed width, but a summary naming several values is not — it has to clip rather than
 * spill past the control's border. `min-width: 0` is what lets it shrink inside the button's flex row;
 * without it the text sets the floor and overflows instead of truncating. The full text stays reachable
 * on hover and through the control's accessible name. */
const TruncatedText = styled.span`
	display: block;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/** Keeps a long field label on one line at the width of the control it labels. */
const LabelRow = styled.div`
	max-width: 220px;
`;

function ValueLabelWarmer({source, onSettled}: {source: ValueSource; onSettled: () => void}): null {
	const {loading} = source.useCandidates('');
	useEffect(() => {
		if (!loading) {
			onSettled();
		}
	}, [loading, onSettled]);
	return null;
}

export interface ConditionTileProps {
	condition: Condition;
	index: number;
	taxonomy: ReportTaxonomy;
	onChangeValues: (index: number, ids: string[], operator?: string) => void;
	onRemove: (index: number) => void;
}

/** Resolves which `ValueSource` registry entry backs this condition's picker. Falls back to
 * `'boolean'` both for genuinely boolean fields and for the non-editable case, where the
 * returned source is never opened but `useValueSource` must still be called with some kind
 * (see the hooks-safety note on `ConditionTile`). */
function resolveKind(taxonomy: ReportTaxonomy, field: ReportTaxonomyField | undefined, editable: boolean): ResolverKind {
	if (!editable || !field || field.dataType === 'boolean') {
		return 'boolean';
	}
	return (resolverKindFor(taxonomy, field) as ResolverKind | undefined) ?? 'boolean';
}

/** One filter condition rendered as a labelled dropdown-field tile with a value picker and a
 * delete affordance. `useValueSource` IS a hook (it formats candidate labels through `useIntl`),
 * so it has to stay an unconditional top-level call here — `resolveKind` returns a usable kind even
 * for a non-editable field precisely so this call never has to be skipped. The Relay/async hooks
 * that actually back a picker only run once `ConditionValuePicker` mounts, i.e. once the dropdown
 * is open. */
export function ConditionTile({condition, index, taxonomy, onChangeValues, onRemove}: ConditionTileProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition} = useReportBuilder();
	const grain = grainOf(definition);
	const field = fieldByFilterField(taxonomy, condition.field);
	const editable = field ? isValueEditable(taxonomy, field) : false;
	const kind = resolveKind(taxonomy, field, editable);
	// `field` is passed so an `enumeratedValues` kind can build its candidates from the vocabulary
	// the catalog publishes on that very field. Every other kind ignores it. `grain` is passed so
	// the `label` kind can pick between the task-label and project-label sources - see
	// `useValueSource`'s own comment for why only that one kind needs it.
	const source = useValueSource(kind, field, grain);
	// Bumped once the label warmer observes the fetch settle, so the summary below is recomputed
	// against the freshly warmed cache even though the cache itself lives outside React state.
	const [, forceSummaryRecompute] = useReducer((count: number) => count + 1, 0);
	const hasValues = condition.values.length > 0;
	const warmLabelsOnMount = editable && hasValues && RELAY_BACKED_KINDS.includes(kind);
	// Remaining work's stored values are a comparison (operator + threshold), not picker ids, so the
	// picker's selection is derived from the condition rather than read off it directly.
	const isRemainingWork = kind === 'remainingWork';
	const selectedIds = isRemainingWork ? remainingWorkSelection(condition) : condition.values;

	// The choices offered come from the field's own catalog entry, never a client-side list - see
	// `operatorsForField`. A field whose catalog declares only one family (e.g. a predicate the
	// upstream engine cannot negate) yields a single option, and the picker itself renders nothing
	// for that case.
	// Memoized: this runs in the render body of every tile, so without it a sibling tile's local
	// state change recomputes the option list for all of them.
	const operatorOptions = useMemo(() => (field ? operatorsForField(taxonomy, field) : []), [taxonomy, field]);
	// The stored operator may be a multi-value member (IN, NOT_IN, ALL_IN, NOT_ALL_IN) while the
	// options are single-value family representatives (IS, IS_NOT, IS_ALL, IS_NOT_ALL) - passing a
	// single-element dummy array here forces the singular form regardless of the condition's actual
	// arity, so the picker's selected segment reflects the family alone.
	const selectedOperator = operatorForValues(condition.operator, ['one']);

	const labels: SummaryLabels = {
		or: formatMessage({id: 'ai_report_builder.conditions.summary_or', defaultMessage: 'or'}),
		and: formatMessage({id: 'ai_report_builder.conditions.summary_and', defaultMessage: 'and'}),
		selected: n =>
			formatMessage(
				{id: 'ai_report_builder.conditions.summary_selected', defaultMessage: '{count} selected'},
				{count: n}
			),
		yes: formatMessage({id: 'common.yes', defaultMessage: 'Yes'}),
		no: formatMessage({id: 'common.no', defaultMessage: 'No'}),
	};

	// Resolved through `fieldForStoredCondition`, not the `field` above, so a condition stored
	// against a filter token the catalog has since retired still shows its human name instead of the
	// raw token. Deliberately the ONLY thing that lookup feeds: `field` stays undefined for such a
	// condition, which is what keeps it non-editable.
	const label = fieldForStoredCondition(taxonomy, condition.field)?.label ?? condition.field;
	// Only pass a resolveLabel for editable fields, where `source` is the real id-backed value
	// source. For non-editable fields `source` is the boolean fallback (see `resolveKind` above),
	// whose resolveLabels never resolves the stored value — passing it here would make every value
	// register as unresolved and collapse the summary to a misleading "N selected" instead of
	// showing the stored value directly.
	// The field name is omitted here: it is already shown by the <Label> directly above this
	// control, and the trigger is only 220px wide - repeating the name would spend the room the
	// operator prefix needs. The chat intent card (the other caller of this function) renders a
	// condition with no label beside it, so it keeps the default.
	const summary = formatConditionSummary(condition, taxonomy, {
		resolveLabel: editable ? id => source.resolveLabels([id])[id] : undefined,
		labels,
		includeFieldName: false,
	});

	// An empty condition has nothing to summarise, and the bare field name it would otherwise
	// show just repeats the label above it. Prompt for a selection instead. The singular/plural
	// split is about arity — how many values the CONTROL can hold — not the field's data type:
	// boolean and remainingWork are both single-value controls (exactly one of two mutually
	// exclusive candidates ever applies), even though remainingWork sits on a number field rather
	// than a boolean one. Every other editable kind backs a genuine multi-select, so it gets the
	// plural wording. Read off `source.singleSelect`, the same flag `ConditionValuePicker` writes
	// through, rather than a second list of kinds here: two independent declarations of one fact
	// drift, and they already had (boolean was named single-value here while its source still let
	// the picker accumulate both answers).
	const placeholder = formatMessage(
		{
			id: source.singleSelect
				? 'ai_report_builder.conditions.select_placeholder'
				: 'ai_report_builder.conditions.select_placeholder_plural',
		},
		{label}
	);
	// Only prompt where the prompt can be acted on. A non-editable condition's control is disabled and
	// cannot be opened, so "Select Project(s)" there asks for something the user has no way to do; its
	// summary is the honest thing to show. This also keeps the wording off the fields the singular/
	// plural split cannot describe, since `resolveKind` returns the boolean kind for every
	// non-editable field regardless of how many values it takes.
	const triggerText = hasValues || !editable ? summary : placeholder;

	return (
		<FlexColumn gap={'xs'} flex={'0 0 auto'} data-cy={`ai-report-condition-tile-${index}`}>
			{warmLabelsOnMount ? <ValueLabelWarmer source={source} onSettled={forceSummaryRecompute} /> : null}
			<LabelRow>
				<Label data-cy={`ai-report-condition-label-${index}`}>
					<TruncatedText title={label}>{label}</TruncatedText>
				</Label>
			</LabelRow>
			<FlexRow alignItems={'center'} gap={'xs'}>
				<DropdownList>
					<DropdownList.Trigger>
						{/* The accessible name normally comes from string children; the summary is wrapped in an
						 * element to truncate it, which would otherwise stringify to '[object Object]', so the
						 * name is supplied explicitly. */}
						<ButtonDropdownField
							width={'220px'}
							disabled={!editable}
							isDefaultLabel={!hasValues && editable}
							// The VISIBLE text omits the field name - it is in the <Label> right above, and the
							// trigger is only 220px wide - but the accessible name cannot. That <Label> is a
							// bare <label> with no htmlFor and it is a sibling of this row, not a wrapper, so
							// nothing associates the two for a screen reader; without the name here, a report
							// filtered on Client, Priority and Labels announces three buttons that all read
							// "Is ...", or past three values just "4 selected".
							//
							// Prefixed only when there is a VALUE summary to name. With no values the trigger
							// already shows the field name by itself - as the placeholder ("Select Progress")
							// or, for a non-editable field, as the bare name - so prefixing would announce it
							// twice.
							aria-label={hasValues ? `${label} ${triggerText}` : triggerText}
							data-cy={`ai-report-condition-field-${index}`}
						>
							<TruncatedText title={triggerText}>{triggerText}</TruncatedText>
						</ButtonDropdownField>
					</DropdownList.Trigger>
					{editable ? (
						// Fixed width instead of `useTriggerWidth`: the trigger is only 220px, but the
						// search field inside `ConditionValuePicker` can't shrink below its own native
						// minimum width, so matching a narrow trigger overflows the popup horizontally.
						<DropdownList.Content offset={4} width={'300px'}>
							<ConditionValuePicker
								source={source}
								selectedIds={selectedIds}
								onChange={ids => {
									if (!isRemainingWork) {
										onChangeValues(index, ids);
										return;
									}
									if (ids.length === 0) {
										onChangeValues(index, []);
										return;
									}
									const {operator, values} = remainingWorkWrite(ids[0]);
									onChangeValues(index, values, operator);
								}}
								operatorPicker={
									<ConditionOperatorPicker
										options={operatorOptions}
										value={selectedOperator}
										onChange={operator =>
											onChangeValues(
												index,
												condition.values,
												operatorForValues(operator, condition.values)
											)
										}
									/>
								}
							/>
						</DropdownList.Content>
					) : null}
				</DropdownList>
				<Button
					iconOnly
					emphasis={'low'}
					icon={'trash'}
					onPress={() => onRemove(index)}
					aria-label={formatMessage({id: 'common.remove'})}
					data-cy={`ai-report-condition-delete-${index}`}
				/>
			</FlexRow>
		</FlexColumn>
	);
}
